import { num } from "@/lib/format";
import { AlertTriangle, Boxes, Package } from "lucide-react";
import StackBreakdown from "./StackBreakdown";

function StackVisual({ height, limit, weight, density, fclass, label, count, accent }) {
  const ratio = Math.min(height / limit, 1.08);
  const over = height > limit;
  const near = height >= 90 && !over;
  const fill = over ? "bg-[hsl(var(--destructive))]" : near ? "bg-[hsl(var(--warning))]" : "bg-[hsl(var(--primary))]";

  return (
    <div className="flex-1 border border-border bg-background p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-sm ${accent}`} />
          <span className="text-sm font-semibold">{label}</span>
        </div>
        <span className="metric-num text-xs text-muted-foreground">×{count}</span>
      </div>

      <div className="mt-3 flex items-end gap-3">
        {/* visual column */}
        <div className="relative h-40 w-16 shrink-0 border-b-2 border-border">
          {/* limit marker */}
          <div className="absolute inset-x-[-6px] top-0 border-t border-dashed border-[hsl(var(--destructive))]/60">
            <span className="absolute -top-3 right-0 text-[9px] font-bold text-[hsl(var(--destructive))]">{num(limit)}"</span>
          </div>
          <div
            className={`absolute bottom-0 left-0 w-full ${fill} transition-all`}
            style={{ height: `${ratio * 100}%` }}
          >
            <div
              className="h-full w-full opacity-30"
              style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 6px, rgba(255,255,255,0.25) 6px, rgba(255,255,255,0.25) 7px)" }}
            />
          </div>
        </div>

        <div className="grid flex-1 grid-cols-2 gap-y-2 text-sm">
          <div>
            <div className="label-caps">Height</div>
            <div className={`metric-num font-bold ${over ? "text-[hsl(var(--destructive))]" : ""}`}>{num(height, 1)}"</div>
          </div>
          <div>
            <div className="label-caps">Weight</div>
            <div className="metric-num font-bold">{num(weight)} lb</div>
          </div>
          <div>
            <div className="label-caps">Density</div>
            <div className="metric-num font-bold">{num(density, 2)}</div>
          </div>
          <div>
            <div className="label-caps">Class</div>
            <div className="metric-num font-bold text-[hsl(var(--primary))]">{fclass ? `${fclass}` : "—"}</div>
          </div>
        </div>
      </div>

      {over && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-[hsl(var(--destructive))]">
          <AlertTriangle className="h-3.5 w-3.5" /> Exceeds height limit
        </div>
      )}
      {near && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-[hsl(var(--warning))]">
          <AlertTriangle className="h-3.5 w-3.5" /> Near {num(limit)}" limit
        </div>
      )}
    </div>
  );
}

export default function StackEngine({ result }) {
  const s = result?.stack_result;

  if (!s) {
    return (
      <div className="flex h-48 flex-col items-center justify-center border border-dashed border-border bg-card text-muted-foreground">
        <Boxes className="mb-2 h-8 w-8 opacity-40" />
        <p className="text-sm">Calculate to visualize the pallet stack configuration.</p>
      </div>
    );
  }

  const dims = (stack) => stack?.dimensions || "—";

  return (
    <div className="space-y-4">
      <div className="border border-border bg-card" data-testid="stack-engine">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="font-heading text-base font-bold tracking-tight">Stack Engine Output</h3>
        <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.nestable ? "border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "border-zinc-500/40 bg-zinc-500/10 text-zinc-400"}`}>
          {s.nestable ? "Nestable" : "Non-Nesting"}
        </span>
      </div>

      {/* summary row */}
      <div className="grid grid-cols-2 gap-px border-b border-border bg-border sm:grid-cols-4">
        {[
          ["Max / Stack", s.enforced_stack_limit],
          ["Full Stacks", s.full_stack_count],
          ["Partial Pieces", s.last_stack_pieces],
          ["Total Footprints", s.total_stack_footprints],
        ].map(([l, v]) => (
          <div key={l} className="bg-card p-3">
            <div className="label-caps">{l}</div>
            <div className="metric-num mt-1 text-xl font-bold">{num(v)}</div>
          </div>
        ))}
      </div>

      <div className="space-y-4 p-4">
        <div className="flex flex-col gap-4 lg:flex-row">
          {s.full_stack ? (
            <StackVisual
              label={`Full Stack — ${dims(s.full_stack)}`}
              count={s.full_stack_count}
              height={s.full_stack.height}
              limit={s.height_limit}
              weight={s.full_stack.weight}
              density={s.full_stack.density}
              fclass={s.full_stack.freight_class}
              accent="bg-[hsl(var(--primary))]"
            />
          ) : null}
          {s.last_stack ? (
            <StackVisual
              label={`Partial Stack — ${dims(s.last_stack)}`}
              count={1}
              height={s.last_stack.height}
              limit={s.height_limit}
              weight={s.last_stack.weight}
              density={s.last_stack.density}
              fclass={s.last_stack.freight_class}
              accent="bg-[hsl(var(--warning))]"
            />
          ) : null}
          {!s.full_stack && !s.last_stack && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Package className="h-4 w-4" /> No stack configuration for this quantity.
            </div>
          )}
        </div>
      </div>
      </div>
      <StackBreakdown stack={s} calculatedClass={result.calculated_class} />
    </div>
  );
}
